import * as React from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material'
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox'
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import ContentDialog from '../Dialogs/ContentDialog'
import { useOrderRefundClaim } from '../../core/hooks/useOrderRefundClaim'
import OrderRefundClaimController from '../../core/controllers/OrderRefundClaimController'
import { refundCountdownText } from '../../core/functions/refundClaim'
import { dateTimeString } from '../../core/functions/dateTime'
import Hosts from '../../core/functions/hosts'
import { IErrorResponse } from '../../core/types/ErrorResponse'
import {
  TOrderRefundClaimAction,
  TOrderRefundClaimActionType,
} from '../../core/types/OrderRefundClaim'

const REFUND_CLAIMED = 'refund_claimed'

const ACTION_LABEL: Record<TOrderRefundClaimActionType, string> = {
  LinkSent: 'Link enviado',
  CanceledByProducer: 'Cancelado pelo produtor',
  CanceledByAdmin: 'Cancelado pela plataforma',
  CanceledByCustomer: 'Cancelado pelo cliente',
}

const actionLabel = (action: TOrderRefundClaimAction) =>
  action.type === 'LinkSent' && action.recipientEmail
    ? `Link enviado a ${action.recipientEmail}`
    : ACTION_LABEL[action.type]

export interface RefundClaimPanelProps {
  orderId?: string
  setError: (value: string) => void
  setSuccess: (value: boolean) => void
  setCopied: (value: boolean) => void
  // Chamado após cancelar: recarregar detalhe e lista (status virou paid)
  onCanceled: () => void
}

export default function RefundClaimPanel({
  orderId,
  setError,
  setSuccess,
  setCopied,
  onCanceled,
}: RefundClaimPanelProps) {
  const { loading, data, error, execute } = useOrderRefundClaim({ orderId })
  const [sending, setSending] = React.useState(false)
  const [confirmOpen, setConfirmOpen] = React.useState(false)
  const [link, setLink] = React.useState('')

  // 400/403/404 = estado provavelmente mudou; o GET é a fonte da verdade
  const handleFailure = (result: IErrorResponse) => {
    setError(result.error)
    if ([400, 403, 404].includes(result.code)) execute()
  }

  const handleSendLink = async () => {
    if (!orderId) return
    setSending(true)
    setError('')
    try {
      const result = await OrderRefundClaimController.sendCancelLink({
        orderId,
      })
      if ((result as IErrorResponse).code) {
        handleFailure(result as IErrorResponse)
        return
      }
      setLink((result as { link: string }).link)
      setSuccess(true)
      execute()
    } finally {
      setSending(false)
    }
  }

  const handleCancel = async (): Promise<boolean> => {
    if (!orderId) return true
    setError('')
    const result = await OrderRefundClaimController.cancel({ orderId })
    if (result) {
      handleFailure(result)
      return true
    }
    setSuccess(true)
    execute()
    onCanceled()
    return true
  }

  const handleCopy = () => {
    Hosts.copyTextToClipboard(link)
    setCopied(true)
  }

  if (loading && !data) {
    return (
      <Box sx={{ p: 2 }}>
        <Skeleton variant="rounded" height={80} />
      </Box>
    )
  }

  if (!data) {
    return error !== '' ? (
      <Box sx={{ p: 2 }}>
        <Alert severity="warning">{error}</Alert>
      </Box>
    ) : null
  }

  // Enviar link não exige permissão (canSendLink=false é só o cooldown);
  // a permissão vale apenas para o botão de cancelar (canCancel)
  const inProgress = data.status === REFUND_CLAIMED
  if (!inProgress && data.actions.length === 0) return null

  return (
    <Stack spacing={2} sx={{ p: 2 }}>
      {inProgress && (
        <>
          <Alert severity="warning">
            {data.expiresAt
              ? refundCountdownText(data.expiresAt)
              : 'Reembolso solicitado pelo cliente'}
            . Enviar o link de cancelamento não estende esse prazo.
          </Alert>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button
              size="small"
              variant="outlined"
              startIcon={<ForwardToInboxIcon />}
              disabled={!data.canSendLink || sending}
              onClick={handleSendLink}
            >
              Enviar link de cancelamento
            </Button>
            {data.canCancel && (
              <Button
                size="small"
                variant="contained"
                color="error"
                startIcon={<CancelOutlinedIcon />}
                disabled={sending}
                onClick={() => setConfirmOpen(true)}
              >
                Cancelar reembolso
              </Button>
            )}
            {data.canCancel && data.isAdmin && (
              <Chip size="small" label="Ação da plataforma" />
            )}
          </Stack>

          {!data.canSendLink && (
            <Typography variant="caption" color="text.secondary">
              Link enviado há pouco tempo, aguarde alguns minutos para
              reenviar.
            </Typography>
          )}

          {link !== '' && (
            <Alert
              severity="success"
              action={
                <Button
                  color="inherit"
                  size="small"
                  startIcon={<ContentCopyIcon />}
                  onClick={handleCopy}
                >
                  Copiar
                </Button>
              }
              sx={{ wordBreak: 'break-all' }}
            >
              E-mail enviado ao cliente. Link: {link}
            </Alert>
          )}
        </>
      )}

      {data.actions.length > 0 && (
        <Stack spacing={1}>
          <Typography variant="subtitle2" fontWeight={700}>
            Histórico do reembolso
          </Typography>
          {data.actions.map((action, i) => (
            <Box key={`${action.type}-${action.createdDate}-${i}`}>
              <Typography variant="body2">{actionLabel(action)}</Typography>
              <Typography variant="caption" color="text.secondary">
                {[
                  action.userName,
                  dateTimeString(new Date(action.createdDate)),
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </Typography>
            </Box>
          ))}
        </Stack>
      )}

      <ContentDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Cancelar reembolso"
        labelPrositive="Cancelar reembolso"
        onPositive={handleCancel}
      >
        <Typography>
          A solicitação será cancelada e a transação voltará para paga. Essa
          ação não pode ser desfeita por aqui.
        </Typography>
      </ContentDialog>
    </Stack>
  )
}
